import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  OperatorActionConfig,
  OperatorActionType,
  useOperatorAction,
} from 'src/hooks/useTriggerOperatorAction';
import { OperatorModalContext } from 'src/context/operatorModalContext';
import {
  Box,
  FormField,
  Modal,
  Select,
  SpaceBetween,
} from '@cloudscape-design/components';
import { PermissionAwareButton } from 'src/components/PermissionAwareButton';
import Button from '@cloudscape-design/components/button';
import { ToastMessageContext } from 'src/context/toastMessageContext';

export interface OperatorOverrideConfig extends OperatorActionConfig {
  operatorStatus: OperatorJobStatus;
}

export const OverrideModal = () => {
  const { triggerOperatorAction } = useOperatorAction();
  const { overrideConfig, setOverrideConfig } =
    useContext(OperatorModalContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [overrideType, setOverrideType] = useState<{
    label: string;
    value: OperatorActionType;
  }>({ label: 'Skip', value: 'SKIPPEDOVERRIDE' });
  const navigate = useNavigate();

  const { addToastMessage } = useContext(ToastMessageContext);

  if (!overrideConfig) return <></>;

  const overrideOptions = [
    { label: 'Skip', value: 'SKIPPEDOVERRIDE' },
    { label: 'Mark as Succeeded', value: 'SUCCEEDEDOVERRIDE' },
  ];

  if (overrideConfig.operatorStatus !== 'FAILED') {
    overrideOptions.push({ label: 'Mark as Failed', value: 'FAILEDOVERRIDE' });
  }

  return (
    <Modal
      header={`Override ${overrideConfig.operatorName} Operator?`}
      visible={!!overrideConfig}
      onDismiss={() => {
        setOverrideConfig(null);
      }}
      footer={
        <Box float="right">
          {error && <p className="error-text">{error.message}</p>}
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              onClick={() => {
                setOverrideConfig(null);
              }}
              variant="link"
            >
              Cancel
            </Button>
            <PermissionAwareButton
              requiredPermission="ADMINISTER"
              actionDescription="override operators"
              onClick={() => {
                setLoading(true);
                setError(null);
                triggerOperatorAction({
                  ...overrideConfig,
                  action: overrideType.value,
                })
                  .then(() => {
                    addToastMessage({
                      header: `Processing Request`,
                      description: `The override request for ${overrideConfig.operatorName} is being processed. This page will refresh in one minute.`,
                      type: 'success',
                      dismissTime: 60000,
                      onDismiss: () => {
                        /* istanbul ignore next */
                        navigate(0);
                      },
                    });
                    setOverrideConfig(null);
                  })
                  .catch(setError)
                  .finally(() => {
                    setLoading(false);
                  });
              }}
              loading={loading}
            >
              Override
            </PermissionAwareButton>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween direction="horizontal" size="xs">
        {overrideConfig.operatorStatus === 'PENDING' && (
          <>
            <p>
              If the operator status is manually overridden, it cannot be
              updated again. Which means, if the corresponding SageMaker job is
              still running in your AWS account and you manually override the
              operator node status, even though later the SageMaker job finishes
              with a different status (e.g. you override the operator node
              status as &quot;Succeeded&quot;, but the SageMaker job actually
              failed later), MLPigeon workflow will not register the actual
              SageMaker job status.
            </p>
            <p>
              If the operator node is marked as <strong>Failed</strong>,
              MLPigeon will stop the workflow and the operator node will no
              longer be retryable.
            </p>
          </>
        )}

        <p>
          If the operator node is marked as <strong>Succeeded</strong> or{' '}
          <strong>Skipped</strong>, MLPigeon will continue the workflow if there
          are downstream operator(s). But if any downstream operators contain a
          flow variable that refer to a parameter of this SageMaker job, the
          desired behavior may not work.
        </p>

        <FormField label="Override type">
          <Select
            selectedOption={overrideType}
            onChange={({ detail }) =>
              setOverrideType({
                label: detail.selectedOption.label ?? 'Skip',
                value:
                  (detail.selectedOption.value as OperatorActionType) ??
                  'SKIPPEDOVERRIDE',
              })
            }
            options={overrideOptions}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};
